import React from 'react';
import {
    Calendar,
    Edit,
    Trash2,
    FileText,
    FileUp,
    ShieldCheck,
    AlertTriangle,
    CheckCircle,
    Clock,
    Wrench,
    XCircle,
    Cpu,
    Thermometer,
    Gauge,
    Droplets
} from 'lucide-react';

import MeteringNodeIcon from './../../../MeteringNodeIcon';

import {
    NODE_STATUS_META,
    getCommissioningState
} from './houseHelpers';

import {
    DeviceSection,
    DeviceListSection
} from './DeviceBlocks';

const statusIcons = {
    CheckCircle,
    Clock,
    Wrench,
    XCircle,
    AlertTriangle
};

const getNodeStatusMeta = (status) => {
    const meta = NODE_STATUS_META[status] || {
        label: 'Не указан',
        badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400',
        icon: 'AlertTriangle'
    };

    return {
        ...meta,
        icon: statusIcons[meta.icon] || AlertTriangle
    };
};

const MeteringNodeCard = ({
    node,
    canChangeHouses,
    onEditStatus,
    onEditNode,
    onDeleteNode,
    onEditDevice,
    onCreateDevice,
    onDeleteDevice,
    onGoToDevice,
    onGenerateCard,
    onViewActs
}) => {
    const statusMeta = getNodeStatusMeta(node.status);
    const StatusIcon = statusMeta.icon;

    const commissioning = getCommissioningState(node);

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-700 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <MeteringNodeIcon className="w-6 h-6 text-blue-400" />

                    <h3 className="text-white font-semibold">
                        {node.name}
                    </h3>

                    {node.description && (
                        <span className="text-slate-400 text-xs ml-2">
                            — {node.description}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${statusMeta.badge}`}
                        title={`Статус: ${statusMeta.label}`}
                    >
                        <StatusIcon className="w-3 h-3" />
                        {statusMeta.label}
                    </span>

                    {node.status === 1 && node.oplombirovanDate && node.oplombirovanValidUntil && (
                        <span className="px-2 py-1 rounded text-xs flex items-center gap-1 bg-slate-700/40 border border-slate-600/50 text-slate-300">
                            <Calendar className="w-3 h-3" />
                            {node.oplombirovanDate} — {node.oplombirovanValidUntil}
                        </span>
                    )}

                    {node.status === 1 && !node.oplombirovanValidUntil && (
                        <span className="px-2 py-1 rounded text-xs flex items-center gap-1 bg-yellow-500/15 border border-yellow-500/40 text-yellow-400">
                            <AlertTriangle className="w-3 h-3" />
                            Срок действия не задан
                        </span>
                    )}

                    {canChangeHouses && (
                        <>
                            <button
                                onClick={onViewActs}
                                className="p-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded transition"
                                title="Просмотр актов ввода/вывода в эксплуатацию"
                            >
                                <FileUp className="w-4 h-4" />
                            </button>

                            <button
                                onClick={onGenerateCard}
                                className="p-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded transition"
                                title="Сформировать карту программирования"
                            >
                                <FileText className="w-4 h-4" />
                            </button>

                            <button
                                onClick={onEditStatus}
                                className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                title="Изменить статус узла"
                            >
                                <Wrench className="w-4 h-4" />
                            </button>

                            <button
                                onClick={onEditNode}
                                className="p-1.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded transition"
                                title="Редактировать узел"
                            >
                                <Edit className="w-4 h-4" />
                            </button>

                            <button
                                onClick={onDeleteNode}
                                className="p-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                title="Удалить узел"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-4">
                <DeviceSection
                    title="Тепловычислитель"
                    icon={Cpu}
                    color="cyan"
                    device={node.heatComputer}
                    canChangeHouses={canChangeHouses}
                    onEdit={() =>
                        node.heatComputer && onEditDevice('heat-computer', node.heatComputer)
                    }
                    onCreate={() => onCreateDevice('heat-computer')}
                    onDelete={() =>
                        node.heatComputer &&
                        onDeleteDevice(
                            'heat-computer',
                            node.heatComputer.id,
                            node.heatComputer.displayName || node.heatComputer.deviceName,
                            node.heatComputer.deviceType
                        )
                    }
                    onGoToDevice={() => node.heatComputer && onGoToDevice(node.heatComputer)}
                />

                <DeviceListSection
                    title="Датчики температуры"
                    icon={Thermometer}
                    color="red"
                    devices={node.temperatureSensors}
                    canChangeHouses={canChangeHouses}
                    onEdit={(sensor) => onEditDevice('temp-sensor', sensor)}
                    onCreate={() => onCreateDevice('temp-sensor')}
                    onDelete={(sensor) =>
                        onDeleteDevice(
                            'temp-sensor',
                            sensor.id,
                            sensor.position || sensor.displayName,
                            'temp-sensor'
                        )
                    }
                />

                <DeviceListSection
                    title="Датчики давления"
                    icon={Gauge}
                    color="purple"
                    devices={node.pressureSensors}
                    canChangeHouses={canChangeHouses}
                    onEdit={(sensor) => onEditDevice('pressure-sensor', sensor)}
                    onCreate={() => onCreateDevice('pressure-sensor')}
                    onDelete={(sensor) =>
                        onDeleteDevice(
                            'pressure-sensor',
                            sensor.id,
                            sensor.position || sensor.displayName,
                            'pressure-sensor'
                        )
                    }
                />

                <DeviceListSection
                    title="Датчики объёмного расхода"
                    icon={Droplets}
                    color="blue"
                    devices={node.flowMeters}
                    canChangeHouses={canChangeHouses}
                    onEdit={(meter) => onEditDevice('flow-meter', meter)}
                    onCreate={() => onCreateDevice('flow-meter')}
                    onDelete={(meter) =>
                        onDeleteDevice(
                            'flow-meter',
                            meter.id,
                            meter.position || meter.displayName,
                            'flow-meter'
                        )
                    }
                />
            </div>
        </div>
    );
};

export default MeteringNodeCard;